// ISDP CUSTOM: Internal "preview as" state. Lets a Dräger employee (draeger/admin)
// look at the external Partner Portal exactly as an external visitor would — e.g. a
// sales rep presenting the partner view to a partner, or a reviewer checking what an
// anonymous visitor really gets. Unlike the localhost-only `devrole` simulator
// (useUserRole.ts), this works in production but is HONORED ONLY for users who are
// really internal (useUserRole checks the real role before applying it), so it can
// never be used to escalate an external user's access.
//
// The preview is not only presentation: while one is active every public-portal fetch
// carries the face in the PREVIEW_HEADER (see previewFetch.ts), and the backend answers as
// that face — so a previewed anonymous visitor receives the anonymous response, not the
// internal one. The backend honours the header only for a real internal caller and only
// to lower access.
//
// Stored in sessionStorage (per-tab) so a rep can keep the internal hub open in one
// tab and the partner preview in another. The last face used is also remembered in
// localStorage so the profile menu's "Partner Portal" entry reopens it.
import {
  ALL_PRODUCTS_PREVIEW,
  isDevHubRole,
  isProductIdShape,
  type DevHubRole,
} from '@internal/plugin-isdp-common';

const KEY = 'devhub-preview-role';
const LAST_KEY = 'devhub-preview-last';

/**
 * The faces an internal user may preview:
 *  - `anonymous`       a visitor who is not signed in
 *  - `public`          signed in, no partner access
 *  - `registered`      a registered partner with no product entitlements
 *  - `partner:<ids>`   a partner entitled to the listed products only
 *  - `partner`         a partner entitled to every product (client-only wildcard)
 *  - `admin`           reveals admin-gated, unreleased portal surfaces; honoured ONLY for a
 *                      real global admin (useUserRole enforces realRole==='admin').
 */
export type PreviewRole =
  | 'anonymous'
  | 'public'
  | 'registered'
  | 'partner'
  | 'admin'
  | `partner:${string}`;

const BASE_FACES: readonly string[] = [
  'anonymous',
  'public',
  'registered',
  'partner',
  'admin',
];
const PARTNER_PREFIX = 'partner:';

/** The product ids of a `partner:<ids>` face (shape-checked, de-duplicated). */
function partnerFaceIds(value: string): string[] {
  const out: string[] = [];
  for (const id of value.slice(PARTNER_PREFIX.length).split(',')) {
    if (isProductIdShape(id) && !out.includes(id)) out.push(id);
  }
  return out;
}

export function isPreviewRole(value: unknown): value is PreviewRole {
  if (typeof value !== 'string') return false;
  if (BASE_FACES.includes(value)) return true;
  return value.startsWith(PARTNER_PREFIX) && partnerFaceIds(value).length > 0;
}

/** The face for "a partner entitled to exactly these products" (none → registered). */
export function partnerPreview(productIds: readonly string[]): PreviewRole {
  const ids = productIds.filter(isProductIdShape);
  return ids.length > 0 ? `partner:${ids.join(',')}` : 'registered';
}

/** The products a `partner:<ids>` face selects, or [] for every other face. */
export function previewSelectedProducts(preview: PreviewRole): string[] {
  return preview.startsWith(PARTNER_PREFIX) ? partnerFaceIds(preview) : [];
}

function read(storage: () => Storage, key: string): PreviewRole | null {
  try {
    const raw = storage().getItem(key);
    return raw && isPreviewRole(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function readPreviewRole(): PreviewRole | null {
  return read(() => sessionStorage, KEY);
}

/** The face this browser last previewed (survives the tab), or null. */
export function readLastPreviewRole(): PreviewRole | null {
  return read(() => localStorage, LAST_KEY);
}

export function writePreviewRole(role: PreviewRole): void {
  try {
    sessionStorage.setItem(KEY, role);
  } catch {
    /* non-fatal */
  }
  try {
    localStorage.setItem(LAST_KEY, role);
  } catch {
    /* non-fatal */
  }
}

export function clearPreviewRole(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* non-fatal */
  }
}

/** Map a preview selection to the effective role used for content gating. */
export function previewToRole(preview: PreviewRole): DevHubRole {
  if (preview === 'anonymous' || preview === 'public') return 'public';
  if (preview === 'admin') return 'admin';
  return 'partner';
}

/** The client-side entitlement list of a previewed face. */
export function previewFaceProducts(preview: PreviewRole): string[] {
  if (preview === 'partner' || preview === 'admin') return [ALL_PRODUCTS_PREVIEW];
  return previewSelectedProducts(preview);
}

/**
 * The PREVIEW_HEADER value that makes the backend answer as this face, or undefined when
 * the face needs none: `partner` (all products) and `admin` are what an internal caller
 * gets anyway — and the client-only wildcard is never sent.
 */
export function previewHeaderValue(preview: PreviewRole): string | undefined {
  if (preview === 'partner' || preview === 'admin') return undefined;
  if (preview.startsWith(PARTNER_PREFIX)) {
    return `${PARTNER_PREFIX}${partnerFaceIds(preview).join(',')}`;
  }
  return preview;
}

/**
 * The client-side entitlement list of a SIMULATED role (the localhost devrole simulator)
 * and of any internal role: every product, as the client-only wildcard, because the
 * product list is backend config. Presentation only: the server enforces access by the
 * real token, and the wildcard can never be stored or issued. A public face gets nothing.
 */
export function previewProducts(role: DevHubRole): string[] {
  return role === 'public' ? [] : [ALL_PRODUCTS_PREVIEW];
}

// Re-exported for callers that persist a raw string.
export { isDevHubRole };
